<template>
  <div class="min-h-screen relative overflow-hidden">
    <!-- Background Image with Dark Transparent Overlay -->
    <div
      class="absolute inset-0 bg-cover bg-center bg-no-repeat"
      :style="{ backgroundImage: `url(${backgroundImage})` }"
    ></div>
    <div class="absolute inset-0 bg-black bg-opacity-70"></div>

    <!-- Content -->
    <div class="relative z-10 min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div class="w-full max-w-3xl">
        <!-- Card -->
        <div>
          <!-- Brand Logo -->
          <div class="text-center mb-8">
            <img
              v-if="currentBrand"
              :src="currentBrand.formLogo"
              :alt="currentBrand.name"
              class="mx-auto w-120 object-contain"
            />
            <h2 class="text-5xl font-medium" :style="{ color: currentBrand?.color }">
              {{ isFalken ? 'Road Hazard Ready' : 'TOTAL CONFIDENCE PLAN' }}
            </h2>
            <p class="mt-6 mb-4 text-2xl text-white">
              Sign In to Your Account
            </p>
            <div class="text-center">
              <p class="text-xl text-white">
                Don't have an account?
                <a
                  href="#"
                  @click.prevent="goToSignUp"
                  class="font-medium hover:underline"
                  :style="{ color: currentBrand?.color }"
                >
                  Sign Up
                </a>
              </p>
            </div>
          </div>

          <!-- Error Message -->
          <div v-if="error && !isEmailVerificationError" class="mb-6 rounded-md bg-red-50 p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm text-red-700">{{ error }}</p>
              </div>
            </div>
          </div>

          <!-- Email Verification Warning -->
          <div v-if="isEmailVerificationError" class="mb-6 rounded-md bg-yellow-50 p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-6 w-6 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path fill-rule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3 w-full">
                <h3 class="text-sm font-medium text-yellow-800">Email Verification Required</h3>
                <p class="mt-2 text-sm text-yellow-700">{{ error }}</p>
                <p class="mt-1 text-xs text-yellow-600">
                  Didn't receive the email or your link has expired?
                </p>

                <p v-if="resendSuccess" class="mt-2 text-sm font-medium text-green-700">
                  {{ resendSuccess }}
                </p>

                <p v-if="resendError" class="mt-2 text-sm text-red-600">
                  {{ resendError }}
                </p>

                <button
                  v-if="!resendSuccess"
                  type="button"
                  :disabled="resendLoading"
                  @click="handleResendVerification"
                  class="mt-3 inline-flex items-center gap-2 rounded-md bg-yellow-100 px-3 py-1.5 text-sm font-medium text-yellow-800 hover:bg-yellow-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <svg v-if="resendLoading" class="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  {{ resendLoading ? 'Sending...' : 'Resend Verification Email' }}
                </button>
              </div>
            </div>
          </div>

          <!-- Login Form -->
          <form @submit.prevent="handleLogin" class="space-y-6">
            <!-- Email -->
            <div>
              <input
                id="email"
                v-model="email"
                type="email"
                placeholder="Email Address*"
                required
                class="w-full px-8 py-5 bg-transparent border-2 rounded-lg focus:outline-none placeholder-white placeholder:text-lg brand-input"
                :style="{
                  borderColor: currentBrand?.color,
                  color: 'white',
                  '--focus-color': currentBrand?.color
                }"
              />
            </div>

            <!-- Password -->
            <div class="relative">
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Password*"
                required
                class="w-full px-8 py-5 pr-14 bg-transparent border-2 rounded-lg focus:outline-none placeholder-white placeholder:text-lg brand-input"
                :style="{
                  borderColor: currentBrand?.color,
                  color: 'white',
                  '--focus-color': currentBrand?.color
                }"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute inset-y-0 right-0 pr-4 flex items-center text-white hover:opacity-80 transition-opacity"
              >
                <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
                  <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-18-18ZM22.676 12.553a11.249 11.249 0 0 1-2.631 4.31l-3.099-3.099a5.25 5.25 0 0 0-6.71-6.71L7.759 4.577a11.217 11.217 0 0 1 4.242-.827c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113Z" />
                  <path d="M15.75 12c0 .18-.013.357-.037.53l-4.244-4.243A3.75 3.75 0 0 1 15.75 12ZM12.53 15.713l-4.243-4.244a3.75 3.75 0 0 0 4.244 4.243Z" />
                  <path d="M6.75 12c0-.619.107-1.213.304-1.764l-3.1-3.1a11.25 11.25 0 0 0-2.63 4.31c-.12.362-.12.752 0 1.114 1.489 4.467 5.704 7.69 10.675 7.69 1.5 0 2.933-.294 4.242-.827l-2.477-2.477A5.25 5.25 0 0 1 6.75 12Z" />
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
                  <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                  <path fill-rule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 0 1 0-1.113ZM17.25 12a5.25 5.25 0 1 1-10.5 0 5.25 5.25 0 0 1 10.5 0Z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>

            <!-- Forgot Password Link -->
            <div class="text-right">
              <a
                href="#"
                @click.prevent="goToForgotPassword"
                class="text-lg font-medium hover:underline"
                :style="{ color: currentBrand?.color }"
              >
                Forgot Password?
              </a>
            </div>

            <!-- Submit Button -->
            <div class="flex justify-center">
              <button
                type="submit"
                :disabled="loading"
                class="px-16 py-3 font-medium text-xl rounded-full hover:opacity-90 transition-opacity"
                :style="{
                  backgroundColor: currentBrand?.buttonColor,
                  color: currentBrand?.buttonTextColor,
                  opacity: loading ? 0.7 : 1
                }"
              >
                {{ loading ? 'Signing in...' : 'Sign In' }}
              </button>
            </div>
          </form>

        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getBrandBySlug } from '@/config/brands'
import { useAuthStore } from '@/stores/auth'
import { emailVerificationService } from '@/services/api/emailVerificationService'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const currentBrand = computed(() => {
  const brandSlug = route.params.brandSlug as string
  return getBrandBySlug(brandSlug)
})

const isFalken = computed(() => currentBrand.value?.name?.toLowerCase() === 'falken')

// eslint-disable-next-line @typescript-eslint/no-var-requires
const backgroundImage = require('@/assets/staticbg.png')

const email = ref('')
const password = ref('')
const loading = ref(false)
const error = ref('')
const showPassword = ref(false)

const resendLoading = ref(false)
const resendSuccess = ref('')
const resendError = ref('')

const isEmailVerificationError = computed(() => {
  return error.value.toLowerCase().includes('verify your email')
})

onMounted(() => {
  // Redirect to brand selection if no brand is selected
  if (!currentBrand.value) {
    router.push({ name: 'brand-selection' })
  }
})

const handleLogin = async () => {
  try {
    loading.value = true
    error.value = ''

    // Validate input
    if (!email.value || !password.value) {
      throw new Error('Please enter both email and password')
    }

    // Validate brand is selected
    if (!currentBrand.value) {
      throw new Error('Please select a brand first')
    }

    // Call login API with brand_id
    await authStore.login({
      email: email.value,
      password: password.value,
      brand_id: currentBrand.value.id
    })

    // Redirect handled by auth store

  } catch (err: any) {
    if (err.response?.data?.message) {
      error.value = err.response.data.message
    } else if (err.message) {
      error.value = err.message
    } else {
      error.value = 'Login failed. Please try again.'
    }
  } finally {
    loading.value = false
  }
}

const handleResendVerification = async () => {
  if (!email.value || !currentBrand.value) return

  resendLoading.value = true
  resendSuccess.value = ''
  resendError.value = ''

  try {
    await emailVerificationService.resendVerification(
      email.value,
      currentBrand.value.id
    )
    resendSuccess.value = 'A new verification link has been sent to your email. Please check your inbox.'
  } catch (err: any) {
    resendError.value = err.response?.data?.message || 'Failed to resend verification email. Please try again.'
  } finally {
    resendLoading.value = false
  }
}

const goToSignUp = () => {
  router.push({
    name: 'signup',
    params: { brandSlug: currentBrand.value?.slug }
  })
}

const goToForgotPassword = () => {
  router.push({
    name: 'forgot-password',
    params: { brandSlug: currentBrand.value?.slug }
  })
}
</script>

<style scoped>
/* Custom focus styles for inputs */
.brand-input:focus {
  outline: none;
  border-width: 2px;
  border-color: var(--focus-color) !important;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.1);
}

/* Placeholder styling */
input::placeholder {
  color: white;
  opacity: 0.7;
}

input:focus::placeholder {
  opacity: 0.5;
}
</style>